<template>
    <v-container fluid>
        <v-row class="my-2">
            <v-col cols="12" class="text-left">
                <v-btn color="primary" dark class="primary" @click="createModal()">
                    <v-icon left dark>fa-plus</v-icon>
                    {{ $t('common.form.add-information') }}
                </v-btn>
            </v-col>
            <v-col cols="12" class="pt-0">
                <v-data-table 
                    :headers="headers" 
                    :items="filteredItems" 
                    class="elevation-1"
                    :footer-props="{
                        'items-per-page-text': $t('dataTable.rowsPerPageText'),
                        'items-per-page-all-text': $t('dataTable.rowsPerPageAllText')
                    }"
                    :no-data-text="$t('dataTable.noDataText')"
                    :headerProps="{
                        'sort-by-text': $t('dataTable.sortByText')
                    }"
                >
                    <template v-slot:top>
                        <v-dialog v-model="pricingDialog" max-width="600px" :transition="false" :persistent="!snack">
                            <v-form ref="form" v-if="selectedPrice">
                                <v-card>
                                    <v-card-title>
                                        <span class="headline">{{ $t('page.practice.create_price_dialog.title') }}</span>
                                    </v-card-title>
                                    <v-card-text>
                                        <v-row class="mt-2">
                                            <v-col cols="12" class="pb-0">
                                                <v-text-field 
                                                    v-model="selectedPrice.price"
                                                    :rules="[
                                                        (v) => $inputRules({ value: v }, 'doubleMySQL') || $t('common.form.custom_field_numeric').replace('[FIELD_NAME]', $t('common.table.price'))
                                                    ]"
                                                    :label="$t('common.table.price')" dense outlined></v-text-field>
                                            </v-col>

                                            <v-col cols="12" class="pb-0">
                                                <v-text-field v-model="selectedPrice.en" :rules="nameRules"
                                                    :label="$t('common.table.message_english_customer')"
                                                    :hint="$t('page.practice.create_dialog.label_en') + ' (' + $t('dashboard.menu.eventDetails.mandatory-optional') + ')'"
                                                    outlined dense persistent-hint></v-text-field>
                                            </v-col>
                                            <v-col cols="12" class="pb-0">
                                                <v-text-field v-model="selectedPrice.fr" :rules="nameRules"
                                                    :label="$t('common.table.message_french_customer')"
                                                    :hint="$t('page.practice.create_dialog.label_fr') + ' (' + $t('dashboard.menu.eventDetails.mandatory-optional') + ')'"
                                                    outlined dense persistent-hint></v-text-field>
                                            </v-col>
                                            <v-col cols="12" class="pb-0">
                                                <v-text-field v-model="selectedPrice.es" :rules="nameRules"
                                                    :label="$t('common.table.message_spanish_customer')"
                                                    :hint="$t('page.practice.create_dialog.label_es') + ' (' + $t('dashboard.menu.eventDetails.mandatory-optional') + ')'"
                                                    outlined dense persistent-hint></v-text-field>
                                            </v-col>

                                            <v-col cols="12" class="pb-0">
                                                <v-text-field v-model="selectedPrice.ageMin" outlined dense
                                                    :rules="[
                                                        (v) => $inputRules({value: v}, 'age') || $t('common.form.custom_field_numeric').replace('[FIELD_NAME]', $t('common.table.min_age'))
                                                    ]"
                                                    :label="$t('common.table.min_age')"></v-text-field>
                                            </v-col>
                                            <v-col cols="12" class="pb-0">
                                                <v-text-field v-model="selectedPrice.ageMax"
                                                    :rules="[
                                                        (v) => $inputRules({value: v}, 'age') || $t('common.form.custom_field_numeric').replace('[FIELD_NAME]', $t('common.table.max_age'))
                                                    ]"
                                                    :label="$t('common.table.max_age')" outlined dense></v-text-field>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                    <v-card-actions>
                                        <v-spacer></v-spacer>
                                        <Alert :snackbar="snackDialog" :notificationMessage="msg_snack_dialog"
                                            @cancel="snackDialog = false" :type="color_snack_dialog" :right="true"
                                            :top="true" class="alert" />
                                        <v-btn color="darken-1" dark small @click="closeModal()">{{
                                            $t('common.form.cancel')
                                        }}</v-btn>
                                        <v-btn v-if="editMode == false" @click="createPracticePrice()" color="darken-1" dark
                                            small class="mr-2">{{
                                                $t('common.form.save') }}</v-btn>
                                        <v-btn v-if="editMode == true" @click="updatePracticePrice()" color="darken-1" dark
                                            small class="mr-2">{{
                                                $t('common.form.update') }}</v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-form>
                        </v-dialog>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-icon small class="mr-2" @click="editModal(item.id)">mdi-pencil</v-icon>
                        <v-icon small @click="confirmDeletePrice(item.id)">mdi-delete</v-icon>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <SGSNotificationConfirm v-if="priceDeleteConfirmationDialog" :notificationMessage="$t('common.confirm-delete')"
            @cancel="priceDeleteConfirmationDialog = false;" @agree="deletePrice" type='warning' />
    </v-container>
</template>
<script>
import SGSNotificationConfirm from "@/components/custom/notification/NotificationConfirm.vue";
import Alert from "@/components/custom/notification/Alert.vue"

export default {
    props: {
        practicePrices: {
            type: Array,
            default: null
        }
    },
    components: { Alert, SGSNotificationConfirm },
    data()
    {
        return {
            snack: false,
            snackDialog: false,
            msg_snack_dialog: '',
            color_snack_dialog: "error",
            priceDeleteConfirmationDialog: false,
            priceToDelete: null,
            pricingDialog: false,
            editMode: false,
            selectedPrice: {
                id: null,
                price: null,
                ageMin: null,
                ageMax: null,
                en: null,
                fr: null,
                es: null
            },
        }
    },
    computed: {
        headers()
        {
            return [
                {
                    text: this.$t("common.table.description"),
                    value: "description"
                },
                {
                    text: this.$t("common.table.price"),
                    value: "price"
                },
                {
                    text: this.$t("common.table.min_age"),
                    value: "ageMin"
                },
                {
                    text: this.$t("common.table.max_age"),
                    value: "ageMax"
                },
                {
                    text: this.$t("common.table.actions"),
                    value: "actions",
                    sortable: false
                }
            ]
        },
        filteredItems()
        {

            if (!this.practicePrices)
            {
                return []
            }

            let filteredPrices = [];
            this.practicePrices.forEach(element =>
            {

                const fP = {
                    id: element.id,
                    price: element.price,
                    ageMin: element.ageMin,
                    ageMax: element.ageMax
                }

                try
                {
                    fP.description = element.translations.find(translation => translation.language.languageCode === this.$i18n.locale).pricingNote;
                } catch {
                    if (element.translations.find(translation => translation.pricingNote))
                    {
                        fP.description = element.translations.find(translation => translation.pricingNote).pricingNote || '';
                    } else
                    {
                        fP.description = '';
                    }
                }

                filteredPrices.push(fP);
            });

            return filteredPrices;
        },
        price()
        {
            return this.selectedPrice.price;
        },
        nameRules()
        {
            let isValid = false;

            if (this.selectedPrice.en && this.selectedPrice.en.trim().length > 0 && this.selectedPrice.en.trim().length < 46) isValid = true;
            if (this.selectedPrice.fr && this.selectedPrice.fr.trim().length > 0 && this.selectedPrice.fr.trim().length < 46) isValid = true;
            if (this.selectedPrice.es && this.selectedPrice.es.trim().length > 0 && this.selectedPrice.es.trim().length < 46) isValid = true;

            if (isValid)
            {
                this.$nextTick(() =>
                {
                    this.$refs.form.resetValidation();
                });

            }

            return [() => !!isValid || `${this.$t('page.practice.create_dialog.only_one_message_is_required')} (${this.$t('dashboard.menu.eventDetails.mandatory-optional')})`]
        },
        priceRules()
        {
            if (isNaN(this.selectedPrice.price))
            {
                return [() => this.$t('common.form.custom_field_numeric').replace('[FIELD_NAME]', this.$t('common.table.price'))]
            }

            return [
                (val) => !!val || this.$t('common.form.custom_field_required').replace('[FIELD_NAME]', this.$t('common.table.price')),
            ]
        },
        minAgeRules()
        {
            return [(val) => 
            {
                if (val >= 0)
                {
                    return true;
                }
                return this.$t('common.form.custom_field_numeric').replace('[FIELD_NAME]', this.$t('common.table.min_age'))

            }];
        },
        maxAgeRules()
        {
            return [
                (val) => {
                    if (val >= 0)
                    {
                        return true;
                    }

                    return this.$t('common.form.custom_field_numeric').replace('[FIELD_NAME]', this.$t('common.table.max_age'))
                }
            ]
        },
    },
    methods: {
        createModal()
        {
            this.editMode = false;
            this.pricingDialog = true;
        },
        closeModal()
        {
            this.editMode = false;
            this.pricingDialog = false;
            this.$refs.form.resetValidation();
            this.unsetSelectedPrice();
        },
        editModal(priceId)
        {
            this.setSelectedPrice(this.practicePrices.find(p => p.id === priceId));
            this.editMode = true;
            this.pricingDialog = true;
        },
        createPracticePrice()
        {
            const isValid = this.$refs.form.validate();
            if (!isValid) return false;

            let practiceObj = {
                price: this.selectedPrice.price,
                ageMin: this.selectedPrice.ageMin,
                ageMax: this.selectedPrice.ageMax,
                languages: []
            };

            if (this.selectedPrice.en)
            {
                practiceObj.languages.push({
                    languageCode: 'en',
                    description: this.selectedPrice.en
                });
            }

            if (this.selectedPrice.fr)
            {
                practiceObj.languages.push({
                    languageCode: 'fr',
                    description: this.selectedPrice.fr
                });
            }

            if (this.selectedPrice.es)
            {
                practiceObj.languages.push({
                    languageCode: 'es',
                    description: this.selectedPrice.es
                });
            }

            this.$emit('createPracticePrice', practiceObj);
            this.closeModal();
        },
        updatePracticePrice()
        {
            const isValid = this.$refs.form.validate();
            if (!isValid) return false;

            let practiceObj = {
                id: this.selectedPrice.id,
                price: this.selectedPrice.price,
                ageMin: this.selectedPrice.ageMin,
                ageMax: this.selectedPrice.ageMax,
                languages: []
            };

            if (this.selectedPrice.en)
            {
                practiceObj.languages.push({
                    languageCode: 'en',
                    description: this.selectedPrice.en
                });
            }

            if (this.selectedPrice.fr)
            {
                practiceObj.languages.push({
                    languageCode: 'fr',
                    description: this.selectedPrice.fr
                });
            }

            if (this.selectedPrice.es)
            {
                practiceObj.languages.push({
                    languageCode: 'es',
                    description: this.selectedPrice.es
                });
            }

            this.$emit('updatePracticePrice', practiceObj);
            this.closeModal();
        },
        setSelectedPrice(price)
        {

            this.selectedPrice.id = price.id;
            this.selectedPrice.price = price.price;
            this.selectedPrice.ageMin = price.ageMin;
            this.selectedPrice.ageMax = price.ageMax;

            price.translations.forEach(translation =>
            {
                if (translation.language.languageCode === 'en')
                {
                    this.selectedPrice.en = translation.pricingNote;
                }
                if (translation.language.languageCode === 'fr')
                {
                    this.selectedPrice.fr = translation.pricingNote;
                }
                if (translation.language.languageCode === 'es')
                {
                    this.selectedPrice.es = translation.pricingNote;
                }
            })
        },
        unsetSelectedPrice()
        {
            this.selectedPrice = {
                id: null,
                price: null,
                ageMin: null,
                ageMax: null,
                en: null,
                fr: null,
                es: null
            };
        },
        confirmDeletePrice(priceId)
        {
            this.priceToDelete = priceId;
            this.priceDeleteConfirmationDialog = true;
        },
        deletePrice()
        {
            this.$emit('deletePracticePrice', this.priceToDelete);
            this.priceToDelete = null;
            this.priceDeleteConfirmationDialog = false;
        }
    },
}
</script>